import { createEntityHooks } from '../core/createEntityHooks'
import type { BaseDocument } from '../core/types'

/**
 * Project document interface
 */
export interface ProjectDocument extends BaseDocument {
  projectKey: string
  name: string
  slug: string
  icon: string | null
  iconType: string | null
  iconColor: string | null
  isDefault: boolean
  parentProjectId: string | null
  startDate: number | null
  endDate: number | null
  ownerId: string | null
  description: string | null
  visibility: 'private' | 'viewOnly' | 'open'
  sortOrder: string | null
  estimationType?: 'none' | 'freeform' | 'fibonacci' | 'tshirt' | 'exponential' | null
  isAccountableEnabled?: boolean
  isWorkItemTypeEnabled?: boolean
  statusMode?: 'simple' | 'advanced' | null
  statusMigrationInProgress?: boolean
  isCyclesEnabled?: boolean
  cycleDurationWeeks?: number | null
  coverImageUrl: string | null
  coverImageType: string | null
  coverImageMeta: string | null
}

/**
 * Project-specific hooks
 */
const hooks = createEntityHooks<ProjectDocument>('projects')

export const useProjects = (filter?: Partial<ProjectDocument>) => {
  const { data, isLoading, db } = hooks.useEntities(filter)
  return { projects: data, isLoading, db }
}

export const useRootProjects = (filter?: Partial<ProjectDocument>) => {
  const { projects, isLoading, db } = useProjects(filter)
  const rootProjects = projects?.filter((p) => !p.parentProjectId) ?? null
  return { projects: rootProjects, isLoading, db }
}

export const useChildProjects = (parentId: string | undefined) => {
  const { projects, isLoading, db } = useProjects()
  const childProjects = parentId
    ? projects?.filter((p) => p.parentProjectId === parentId) ?? null
    : null
  return { projects: childProjects, isLoading, db }
}

export const useProject = (id: string | undefined) => {
  const { data, isLoading } = hooks.useEntity(id)
  return { project: data, isLoading }
}

export const useProjectMutations = () => {
  const { create, update, delete: deleteEntity } = hooks.useMutations()
  return {
    createProject: create,
    updateProject: update,
    deleteProject: deleteEntity,
  }
}
